import { io, Socket } from 'socket.io-client';

/**
 * Singleton service to manage Socket.IO connection to the server.
 * Ensures only one socket connection exists across the application.
 */
class SocketService {
  private socket: Socket | null = null;
  private connectionAttempted = false;

  /**
   * Establishes a connection to the Socket.IO server.
   * Uses environment variable REACT_APP_API_URL for the base URL.
   * @returns {Socket} The socket instance
   */
  connect(): Socket {
    if (this.socket?.connected) {
      return this.socket;
    }

    if (this.socket && this.connectionAttempted) {
      return this.socket;
    }
    // eslint-disable-next-line
    const baseUrl = process.env.REACT_APP_API_URL || '';
    // Remove any trailing path from the API URL to get the base server URL
    const serverUrl = baseUrl.replace(/\/api\/v\d+\/?$/, '');

    this.socket = io(serverUrl, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    this.connectionAttempted = true;

    this.socket.on('connect', () => {
      console.warn('[SocketService] Connected to server');
    });

    this.socket.on('disconnect', (reason: string) => {
      console.warn('[SocketService] Disconnected:', reason);
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('[SocketService] Connection error:', error.message);
    });

    return this.socket;
  }

  /**
   * Disconnects from the Socket.IO server.
   * @returns {void}
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connectionAttempted = false;
      console.warn('[SocketService] Manually disconnected');
    }
  }

  /**
   * Gets the current socket instance.
   * @returns {Socket | null} The socket instance or null if not connected
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Checks if the socket is currently connected.
   * @returns {boolean} True if connected, false otherwise
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

// Export singleton instance
export const socketService = new SocketService();
export default socketService;
